<template>
    <Dialog
        :visible="open"
        modal
        :header="title"
        :closable="!busy"
        :dismissable-mask="false"
        :draggable="false"
        class="action-confirm"
        :style="{ width: '28rem', maxWidth: '92vw' }"
        @update:visible="onVisible"
    >
        <p class="action-confirm__message">{{ message }}</p>
        <p v-if="error" class="action-confirm__error" role="alert">{{ error }}</p>
        <template #footer>
            <Button label="Cancel" severity="secondary" variant="outlined" :disabled="busy" @click="cancel" />
            <Button
                :label="confirmLabel"
                :severity="destructive ? 'danger' : 'primary'"
                :loading="busy"
                class="action-confirm__confirm"
                @click="emit('confirm')"
            />
        </template>
    </Dialog>
</template>

<script setup>
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'

defineOptions({ name: 'ActionConfirmDialog' })

// The confirm every destructive action on the identity screens runs through: what will happen,
// in plain words, and one button that does it. A failed attempt answers inside the open dialog
// and confirming again retries — never a toast (identity effort ruling 2026-09-21). The owner
// closes it on success by setting `open` false; this component closes it only on Cancel.
const props = defineProps({
    title: { type: String, required: true },
    message: { type: String, required: true },
    confirmLabel: { type: String, default: 'Confirm' },
    destructive: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    // The last attempt's verdict, shown inside the dialog; empty for none.
    error: { type: String, default: '' }
})

const open = defineModel('open', { type: Boolean, default: false })

const emit = defineEmits(['confirm'])

function cancel() {
    open.value = false
}

// The dialog's own close (the header's x, or Escape) counts as a cancel; it is withheld while busy.
function onVisible(visible) {
    if (visible === false && props.busy === false) {
        open.value = false
    }
}
</script>

<style scoped>
.action-confirm__message {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--p-text-color);
}

.action-confirm__error {
    margin: 12px 0 0;
    font-size: 13px;
    color: var(--p-status-danger);
}
</style>
